import { useCallback, useEffect, useRef, useState } from "react";
import { getDeviceSize } from "../../exports/InteractiveUtils";
import ServerRequest from "../../exports/ServerRequest";
import PerformanceLineChart from "../Body/PerformanceChart";
import Pulse from "../Loader/Pulse";

const RANGE_ARRAY = [
    { title: "1M", value: "1M" },
    { title: "3M", value: "3M" },
    { title: "6M", value: "6M" },
    { title: "1Y", value: "1Y" },
    { title: "5Y", value: "5Y" },
    { title: "ALL", value: "ALL" },
];

const DEFAULT_BENCHMARK = "Nifty_50";

function MFChartContainer({
    selectedData,
    selectedBenchmark = DEFAULT_BENCHMARK,
    onChangeLoading,
    chartType,
    isSuperuser,
}) {
    const controllerRef = useRef();
    const [chartLoading, setChartLoading] = useState(true);
    const [range, setRange] = useState("ALL");
    const [chartProps, setChartProps] = useState(null);
    const [error, setError] = useState(null);
    const [combinedMapData, setCombinedMapData] = useState(null);
    const chartContainerRef = useRef(null);
    const [chartDimension, setChartDimension] = useState({ width: 0, height: 0 });
    const [isReady, setIsReady] = useState(false);


    // useEffects
    useEffect(() => {
        chartLoading ? onChangeLoading?.(true) : onChangeLoading?.(false)
    }, [chartLoading])

    useEffect(() => {
        if (!selectedData) return;

        setChartLoading(true);

        const controller = new AbortController();
        controllerRef.current = controller;

        async function fetchData() {
            try {
                const [navRes, benchmarkRes] = await Promise.all([
                    ServerRequest({
                        url: chartType === "watchlist"
                            ? `/mf/navdata/get/${selectedData}`
                            : isSuperuser
                                ? `/mf/portdata?account_code=${selectedData}`
                                : `/mf/portdata?token=${selectedData}`,
                        method: "GET",
                        signal: controller.signal,
                    }),
                    selectedBenchmark
                        ? ServerRequest({
                            url: `/mf/benchmark/get/${selectedBenchmark}`,
                            method: "GET",
                            signal: controller.signal,
                        })
                        : { data: { BenchmarkData: [] } },
                ]);

                const navData = navRes.data?.navData || [];
                const benchmarkData = benchmarkRes.data?.BenchmarkData || [];

                console.log(navData, benchmarkData)
                setRange("ALL")
                // Combine NAV + Benchmark once
                const combined = mapNAVandBenchmark(navData, benchmarkData);
                setCombinedMapData(combined);
            } catch (err) {
                if (err.name !== "AbortError") setError("Failed to load chart data");
            } finally {
                // setChartLoading(false);
            }
        }

        fetchData();
        return () => controller.abort();
    }, [selectedData, selectedBenchmark, chartType]);

 useEffect(() => {
    if (!combinedMapData) return;

    const rebasedData = sliceAndRebase(combinedMapData, range);
    const formatted = formatCombinedChartData(rebasedData);

    setChartProps({ chart_data: formatted });
    setChartLoading(false);

    // ➜ Only now mark chart as ready
    setIsReady(true);
}, [combinedMapData, range]);


    useEffect(() => {
        if (!chartContainerRef.current) return;

        const updateSize = () => {
            const rect = chartContainerRef.current.getBoundingClientRect();
            setChartDimension({ width: rect.width, height: rect.height });
        };

        requestAnimationFrame(updateSize);

        const resizeObserver = new ResizeObserver(updateSize);
        resizeObserver.observe(chartContainerRef.current);

        return () => resizeObserver.disconnect();
    }, []);

    // Mapping Nav and Benchmark data
    const mapNAVandBenchmark = (navData, benchmarkData) => {
        if (!navData || !benchmarkData) return [];

        const benchmarkMap = new Map();
        benchmarkData.forEach(item => {
            benchmarkMap.set(item.date, item.nav);
        });

        const combined = navData.map(item => ({
            date: item.date,
            fundNav: item.nav ?? item.portNav,
            benchmarkNav: benchmarkMap.get(item.date) ?? null,
        }));

        return combined;
    };

    // Formatting the combined data to cahrt format
    function formatCombinedChartData(data) {
        return data
            .map((item) => {
                const dateObj = new Date(item.date);
                const nav = parseFloat(item.fundNav);
                const benchmark = item.benchmarkNav !== null ? parseFloat(item.benchmarkNav) : null;

                return {
                    date: dateObj,
                    timestamp: dateObj.getTime(),
                    nav: isNaN(nav) ? null : nav,
                    benchmark: isNaN(benchmark) ? null : benchmark,
                    open: isNaN(nav) ? null : nav,
                    high: isNaN(nav) ? null : nav,
                    low: isNaN(nav) ? null : nav,
                    close: isNaN(nav) ? null : nav,
                    volume: 0,
                };
            })
            .filter(d => d.nav !== null);
    }

    // Funtion To Slice the Data
    const ranges = {
        '1M': { months: 1, forwardDays: 0 },
        '3M': { months: 3, forwardDays: 0 },
        '6M': { months: 6, forwardDays: 0 },
        '1Y': { months: 12, forwardDays: 0 },
        '5Y': { months: 60, forwardDays: 0 },
        'ALL': { months: Infinity, forwardDays: 0 }
    };

    function sliceAndRebase(data, rangeKey) {
        if (!data.length) return [];

        const range = ranges[rangeKey];
        if (!range) {
            console.warn(`Invalid range key "${rangeKey}". Falling back to full data.`);
            return [...data];
        }

        const { months, forwardDays = 7 } = range;

        const latestDate = new Date(data[data.length - 1].date);
        const startDate = new Date(latestDate);
        if (months !== Infinity) {
            startDate.setMonth(startDate.getMonth() - months);
        } else {
            startDate.setTime(new Date(data[0].date).getTime());
        }

        let sliced = data.filter(d => new Date(d.date) >= startDate);

        if (sliced.length === 0) sliced = [...data];

        const base = sliced.find(d => d.fundNav != null && d.benchmarkNav != null);
        const rebaseFactor = base ? base.fundNav / base.benchmarkNav : 1;

        const rebased = sliced.map(d => ({
            ...d,
            benchmarkNav:
                d.benchmarkNav != null ? +(d.benchmarkNav * rebaseFactor).toFixed(4) : null
        }));

        const lastDate = new Date(data[data.length - 1].date);
        const forward = Array.from({ length: forwardDays }, (_, i) => {
            const nextDate = new Date(lastDate);
            nextDate.setDate(nextDate.getDate() + i + 1);
            return {
                date: nextDate.toISOString().split('T')[0],
                fundNav: null,
                benchmarkNav: null
            };
        });

        const finalData = [...rebased, ...forward];
        // === Downsample if necessary while preserving first and last ===
        let downsampleInterval = 1;
        if (finalData.length > 5000) {
            downsampleInterval = 22;
        } else if (finalData.length > 1500) {
            downsampleInterval = 7;
        }

        if (downsampleInterval > 1) {
            const lastIndex = finalData.length - 1;
            return finalData.filter((_, index) =>
                index === 0 || index === lastIndex || index % downsampleInterval === 0
            );
        }
        return finalData;
    }

    const seriesConfig = [
        {
            yAccessor: (d) => d.nav,
            stroke: "#2563eb",
            strokeWidth: 2,
            type: "Line",
            isPrimary: true,
        },
        {
            yAccessor: (d) => d.benchmark,
            stroke: "#C4C4C4",
            strokeWidth: 2,
            type: "Line",
        },
    ];

    const yExtentsCalculator = (data) => {
        if (!data || data.length === 0) return;

        const nData = Array.isArray(data) ? data : [data];

        const values = nData.flatMap(d => [d.nav, d.benchmark])
            .filter(v => typeof v === "number" && !isNaN(v));

        if (values.length === 0) return;

        const min = Math.min(...values);
        const max = Math.max(...values);
        const padding = (max - min) * 0.5;

        const lower = Math.max(min - padding);
        const upper = max + padding;

        return [lower, upper];
    };

    return (
        <>
            <div className="mf__chart__container">
                <div className="mf__chart__header"></div>
                <div className="mf__chart__wrapper">
                    <div ref={chartContainerRef} style={{ width: "100%", height: "95%", position: "relative" }}>
                        {!isReady? (
                            <div className="mf__chart__loading-container">
                                <Pulse />
{/* <p>Loading chart data...</p> */}
                            </div>
                        ) : (
                            <div className="mf__chart">
                                {chartProps?.chart_data?.length > 0 && (
                                    <>
                                        <PerformanceLineChart
                                            data={chartProps.chart_data}
                                            height={chartDimension.height}
                                            width={chartDimension.width}
                                            seriesConfig={seriesConfig}
                                            yExtentsCalculator={yExtentsCalculator}
                                            xAccessor={(d) => d.timestamp}
                                        />
                                        <div className="mf-chart-range">
                                            {RANGE_ARRAY.map((r) => (
                                                <RangeBox
                                                    key={r.title}
                                                    range={r}
                                                    active={range === r.value}
                                                    setRange={setRange}
                                                    disabled={chartLoading}
                                                />
                                            ))}
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

function RangeBox({ range, active, setRange, disabled }) {
    return (
        <div
            className={`mf-range-value ${active ? "active__range" : ""} ${disabled ? "disabled" : ""}`}
            onClick={() => !disabled && setRange(range.value)}
        >
            {range.title}
        </div>
    );
}

export default MFChartContainer;
